import { durableGet, durableSet } from "./binding";

/**
 * A tiny breadcrumb trail that survives a reload, so a TV that bounces back to
 * the pairing screen can say WHY instead of us guessing.
 *
 * The pairing screen has exactly three ways to be reached from a paired state,
 * and from the outside they look identical (screen flashes, code screen
 * appears):
 *
 *   - the binding was never read back after the redeem  → storage
 *   - the first heartbeat came back 401                 → token/auth
 *   - an `unpair` realtime event arrived                → CMS/server
 *
 * Each writes a breadcrumb here; the pairing screen renders the last few in its
 * bottom meta strip. Deliberately OUTSIDE the `foyer.` namespace so the
 * checkout wipe doesn't clear it (see lib/checkoutWipe.ts), and stored in the
 * durable store so it survives the native shell's storage too.
 */

const TRACE_KEY = "foyerBootTrace";
const MAX = 6;

export interface BootCrumb {
  /** Short machine-ish reason, e.g. "heartbeat_401". */
  at: string;
  /** Wall-clock, HH:MM:SS - enough to order events across a reload. */
  t: string;
  /** Optional extra (status code, key presence). */
  d?: string;
}

function hhmmss(): string {
  const now = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(now.getHours())}:${p(now.getMinutes())}:${p(now.getSeconds())}`;
}

export function noteBoot(at: string, d?: string): void {
  try {
    const crumbs = readBootTrace();
    crumbs.push({ at, t: hhmmss(), ...(d ? { d } : {}) });
    durableSet(TRACE_KEY, JSON.stringify(crumbs.slice(-MAX)));
  } catch {
    // Never let diagnostics break the boot path.
  }
}

export function readBootTrace(): BootCrumb[] {
  try {
    const raw = durableGet(TRACE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as BootCrumb[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** One-line render for the pairing screen's meta strip. */
export function formatBootTrace(): string {
  return readBootTrace()
    .map((c) => `${c.t} ${c.at}${c.d ? `(${c.d})` : ""}`)
    .join("  ·  ");
}
